'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { usePathname } from 'next/navigation';
// Framer Motion removed - using CSS transitions for better performance
import { Camera } from 'lucide-react';
import { VideoLoader, getProductionVideoConfig, extractFallbackUrls, getMediaTypeFromUrl } from '@/lib/video-utils';
import { ResponsiveBackgroundImage } from '@/types/sections';
import { useResponsiveVideo } from '@/hooks/useResponsiveVideo';
import { getBrandColor } from '@/config/brand-colors';
import { MEDIA_CACHE_VERSION } from '@/config/media-config';
import { videoPosterCache } from '@/lib/video-poster-cache';
import { isVideoAlreadyHandled, markVideoLoading, markVideoComplete } from '@/lib/video-load-deduplication';
import { generateImageSrcSet, getCanonicalMediaUrl } from '@/lib/url-normalization';
import { shouldDisableAutoplayVideos, useNetworkInfo, isSlowConnection } from '@/lib/network-utils';
import { isMobileDeviceSync, isMobileUserAgent } from '@/lib/utils/mobile-detection';
import { playVideoWithMobileSupport, setupVideoForMobileAutoplay, tryPlayVideoImmediate } from '@/lib/video/video-mobile-autoplay';
import { heroVideoState } from '@/lib/hero-video-state';

// Helper to check if a video is already loading or was recently loaded
const checkVideoAlreadyHandled = (normalizedUrl: string): boolean => {
  return isVideoAlreadyHandled(normalizedUrl);
};

// Use unified mobile autoplay helper
const playVideoIdle = (video: HTMLVideoElement, onError?: (error: Error) => void, isMobile?: boolean): void => {
  playVideoWithMobileSupport(video, onError, isMobile);
};

export type BackgroundVideoPriority = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW' | 'BACKGROUND';

export interface BackgroundVideoProps {
  videoUrl: string | ResponsiveBackgroundImage;
  className?: string;
  gradientOverlayClassName?: string;
  disableVideo?: boolean;
  disableFallback?: boolean;
  poster?: string;
  /**
   * Fallback image URL - shown while video loads instead of black background
   * This is typically a generated frame from the video or a manually uploaded image
   * Can be a string or responsive object with mobile/desktop variants
   * REQUIRED for proper loading experience - always use fallbackImage
   */
  fallbackImage?: string | ResponsiveBackgroundImage;
  /**
   * Loading priority - determines preload strategy:
   * - CRITICAL: preload="auto" + immediate load (hero sections)
   * - HIGH: preload="metadata" + early load
   * - NORMAL: preload="metadata" + lazy load (via IntersectionObserver)
   * - LOW/BACKGROUND: preload="none" + lazy load
   * Defaults to CRITICAL for backward compatibility with hero sections
   */
  priority?: BackgroundVideoPriority;
  // Optional telemetry hook
  onVideoEvent?: (event: {
    type: 'attempt' | 'loaded' | 'error' | 'play' | 'stalled' | 'autoplay_blocked' | 'fallback_shown';
    detail?: Record<string, string | number | boolean>;
  }) => void;
}

export function BackgroundVideo({
  videoUrl,
  className = 'absolute inset-0',
  gradientOverlayClassName = 'absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/30 pointer-events-none',
  disableVideo = false,
  disableFallback = false,
  poster = '',
  fallbackImage,
  priority = 'CRITICAL', // Default to CRITICAL for backward compatibility with hero sections
  onVideoEvent,
}: BackgroundVideoProps) {
  // OPTIMIZATION: Network-aware loading - disable autoplay on slow connections
  // CRITICAL: For CRITICAL priority videos (Hero sections), ALWAYS load regardless of network
  // This ensures Hero videos show on mobile devices even on slower connections
  const networkInfo = useNetworkInfo();
  const networkShouldDisableAutoplay = shouldDisableAutoplayVideos();
  // CRITICAL videos bypass network checks - they should always load
  const shouldDisableAutoplay = priority === 'CRITICAL' ? false : networkShouldDisableAutoplay;
  
  // Check if connection is slow enough to show WebP fallback instead of video
  const [isSlow, setIsSlow] = useState(false);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsSlow(isSlowConnection());
    }
  }, []);
  
  // Compute preload strategy based on priority and network conditions
  // OPTIMIZED: Use 'none' for non-critical videos to improve PageSpeed score
  // CRITICAL videos use 'metadata' + programmatic load() for better control
  // CRITICAL: Hero sections (CRITICAL priority) ALWAYS use 'metadata' regardless of network
  const preloadStrategy = (() => {
    // CRITICAL videos always load, bypass network checks
    if (priority === 'CRITICAL') {
      return 'metadata';
    }
    
    // If network is slow, use 'none' to avoid loading videos (for non-CRITICAL videos)
    if (shouldDisableAutoplay) {
      return 'none';
    }
    
    switch (priority) {
      case 'HIGH': return 'metadata';
      case 'NORMAL': return 'none'; // OPTIMIZED: Use 'none' for normal priority to reduce initial payload
      case 'LOW':
      case 'BACKGROUND': return 'none';
      default: return 'none'; // Changed from 'metadata' - default to 'none' for better performance
    }
  })();
  
  // Determine if video should load immediately (skip lazy loading)
  // CRITICAL: Hero sections (CRITICAL priority) ALWAYS load immediately, regardless of network
  // OPTIMIZATION: Don't load immediately if network is slow (for non-CRITICAL videos)
  const shouldLoadImmediately = priority === 'CRITICAL' || (!shouldDisableAutoplay && priority === 'HIGH');
  const videoRef = useRef<HTMLVideoElement>(null);
  const onVideoEventRef = useRef(onVideoEvent);

  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  
  // Track when fallback image first appears and when video is ready
  const fallbackImageStartTime = useRef<number | null>(null);
  const [videoReadyForPlayback, setVideoReadyForPlayback] = useState(false);
  const [canHideFallback, setCanHideFallback] = useState(false);
  
  // CSS class state for opacity transitions (replaces Framer Motion)
  const [fallbackImageOpacity, setFallbackImageOpacity] = useState(1);
  const [videoOpacity, setVideoOpacity] = useState(0);
  const [gradientOverlayOpacity, setGradientOverlayOpacity] = useState(0.7);
  const [darkBackgroundOpacity, setDarkBackgroundOpacity] = useState(0);
  
  // First frame thumbnail state - extracted from video
  const [firstFrameUrl, setFirstFrameUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hasExtractedFrame = useRef(false);
  // Hidden video element for early frame extraction (before main video loads)
  const hiddenVideoRef = useRef<HTMLVideoElement | null>(null);
  const isExtractingFrame = useRef(false);
  
  // If no fallbackImage prop is provided, try to extract one immediately
  const [clientExtractedFallback, setClientExtractedFallback] = useState<string | null>(null);
  
  // Lazy loading state - only load when in viewport for non-critical videos
  // CRITICAL: Hero sections (CRITICAL priority) are always "in view" - load immediately
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(priority === 'CRITICAL' ? true : shouldLoadImmediately);

  // Mobile detection - use synchronous for CRITICAL priority (no delay)
  // For CRITICAL videos, use synchronous detection to avoid loading delays
  const isMobileSync = isMobileDeviceSync();
  const [isMobile, setIsMobile] = useState(isMobileSync);
  const [hasDecidedMobile, setHasDecidedMobile] = useState(priority === 'CRITICAL' ? true : false);
  

  useEffect(() => {
    // For CRITICAL priority, mobile detection is already done synchronously
    if (priority === 'CRITICAL') {
      setIsMobile(isMobileSync);
      setHasDecidedMobile(true);
      return;
    }

    // For other priorities, use async detection (less critical)
    const checkMobile = () => {
      const newIsMobile = isMobileDeviceSync();
      setIsMobile(newIsMobile);
      setHasDecidedMobile(true);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [priority, isMobileSync]);

  // IntersectionObserver for lazy loading non-critical videos
  // This prevents multiple videos from loading simultaneously and overwhelming the network
  useEffect(() => {
    // CRITICAL: Hero sections (CRITICAL priority) ALWAYS load immediately - skip lazy loading
    // Skip lazy loading for CRITICAL/HIGH priority videos (load immediately)
    if (priority === 'CRITICAL' || shouldLoadImmediately) {
      setIsInView(true);
      return;
    }

    // For non-critical videos, use standard rootMargin
    // (CRITICAL/HIGH videos already returned above, so this is only for NORMAL/LOW/BACKGROUND)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Disconnect once video enters viewport
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Standard margin for non-critical videos
        threshold: 0.1,
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [shouldLoadImmediately]);


  // Get current video URL using responsive video hook
  const currentVideoUrl = useResponsiveVideo(
    typeof videoUrl === 'string' ? videoUrl : videoUrl
  );

  // Normalize to canonical URL (without version params) for deduplication
  // This prevents duplicate downloads when the same file is loaded multiple times
  // CRITICAL: For CRITICAL priority videos, ensure we always have a URL (don't return empty string)
  const normalizedVideoUrl = useMemo(() => {
    if (!currentVideoUrl) {
      // For CRITICAL videos, if currentVideoUrl is not ready yet, try to resolve from videoUrl directly
      if (priority === 'CRITICAL' && videoUrl) {
        if (typeof videoUrl === 'string') {
          return getCanonicalMediaUrl(videoUrl) || videoUrl;
        }
        // For responsive videos, use mobile detection to get the right URL immediately
        const mobileUrl = isMobileDeviceSync() 
          ? (videoUrl.mobile || videoUrl.desktop)
          : (videoUrl.desktop || videoUrl.mobile);
        if (mobileUrl) {
          return getCanonicalMediaUrl(mobileUrl) || mobileUrl;
        }
      }
      return '';
    }
    return getCanonicalMediaUrl(currentVideoUrl) || currentVideoUrl;
  }, [currentVideoUrl, priority, videoUrl]);

  // Extract fallback URLs from videoUrl if it's ResponsiveBackgroundImage
  // Normalize them to canonical URLs (without version params) for deduplication
  const videoFallbackUrls = useMemo(() => {
    if (!videoUrl || typeof videoUrl === 'string') return [];
    const fallbacks = extractFallbackUrls(videoUrl, isMobile);
    
    // Normalize each fallback URL to canonical form for deduplication
    return fallbacks.map(url => {
      if (!url) return url;
      return getCanonicalMediaUrl(url) || url;
    });
  }, [videoUrl, isMobile]);

  // Track pathname to reset on route changes (important for client-side navigation)
  const pathname = usePathname();
  const previousPathnameRef = useRef<string | null>(null);

  // Initialize state once and reload video when URL changes OR route changes
  useEffect(() => {
    // Reset all state when video URL changes
    setVideoLoaded(false);
    setVideoError(false);
    setAutoplayBlocked(false);
    setHasPlayed(false);
    setFirstFrameUrl(null);
    setClientExtractedFallback(null);
    hasExtractedFrame.current = false;
    isExtractingFrame.current = false;
    
    // Explicitly update video src when URL changes (for responsive videos)
    // CRITICAL: For CRITICAL priority videos, ensure src is set immediately
    const video = videoRef.current;
    if (video && normalizedVideoUrl && normalizedVideoUrl.trim() !== '') {
      // Only update if src is different to avoid unnecessary reloads
      if (video.src !== normalizedVideoUrl) {
      video.src = normalizedVideoUrl;
        // For CRITICAL videos, call load() immediately to start loading
        if (priority === 'CRITICAL') {
          video.load();
    }
      }
    }
  }, [normalizedVideoUrl, priority]);

  // Also reset video state when route changes (for client-side navigation)
  // This ensures videos reload properly when navigating between pages
  useEffect(() => {
    const currentPathname = pathname || '/';
    const previousPathname = previousPathnameRef.current;
    
    // Only reset if pathname actually changed (not on initial mount)
    if (previousPathname !== null && previousPathname !== currentPathname) {
      // Route changed - reset video state to force reload
      setVideoLoaded(false);
      setVideoError(false);
      setAutoplayBlocked(false);
      setHasPlayed(false);
      setFirstFrameUrl(null);
      setClientExtractedFallback(null);
      setVideoReadyForPlayback(false);
      setCanHideFallback(false);
      hasExtractedFrame.current = false;
      isExtractingFrame.current = false;
      
      // Reset video element if it exists
      const video = videoRef.current;
      if (video && normalizedVideoUrl) {
        // Force video to reload on route change
        video.pause();
        video.src = '';
        video.load(); // Clear current load
        // Use requestAnimationFrame to ensure DOM is ready before setting new src
        requestAnimationFrame(() => {
          if (video && normalizedVideoUrl && videoRef.current === video) {
            video.src = normalizedVideoUrl;
            video.load();
          }
        });
      }
    }
    
    // Update ref for next comparison
    previousPathnameRef.current = currentPathname;
  }, [pathname, normalizedVideoUrl]);

  // Track when video element src changes from JSX
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
  }, [isInView, normalizedVideoUrl]);

  // Extract first frame EARLY using a hidden video element (before main video loads)
  // This provides an immediate visual placeholder while the main video loads
  // If no fallbackImage prop is provided, extract one immediately as a client-side fallback
  // For CRITICAL priority videos (like Hero), start extraction immediately even if not in view
  useEffect(() => {
    if (!normalizedVideoUrl) return;
    
    // For non-critical videos, wait until in view
    if (priority !== 'CRITICAL' && !isInView) return;
    
    // If we already have a resolved fallback image from props (and it's actually an image, not a video URL), skip extraction
    // Note: resolvedFallbackImageUrl is computed below, so we need to resolve it here too
    let resolvedFallback = null;
    if (fallbackImage) {
      if (typeof fallbackImage === 'string') {
        resolvedFallback = fallbackImage;
      } else if (typeof fallbackImage === 'object' && fallbackImage !== null) {
        resolvedFallback = isMobile 
          ? (fallbackImage.mobile || fallbackImage.desktop)
          : (fallbackImage.desktop || fallbackImage.mobile);
      }
    }
    
    if (resolvedFallback) {
      const mediaType = getMediaTypeFromUrl(resolvedFallback);
      // Only skip if it's actually an image URL, not a video URL
      if (mediaType !== 'video') {
        return;
      }
      // If fallbackImage is a video URL, continue to extract firstFrameUrl as fallback
    }
    
    // If we already extracted a client-side fallback, skip
    if (clientExtractedFallback) {
      return;
    }
    
    if (hasExtractedFrame.current || isExtractingFrame.current) return;
    if (poster) {
      // If poster is provided, use it directly
      setFirstFrameUrl(poster);
      hasExtractedFrame.current = true;
      return;
    }

    // Create hidden video element for early frame extraction
    if (!hiddenVideoRef.current) {
      const hiddenVideo = document.createElement('video');
      hiddenVideo.style.position = 'absolute';
      hiddenVideo.style.visibility = 'hidden';
      hiddenVideo.style.width = '1px';
      hiddenVideo.style.height = '1px';
      hiddenVideo.style.pointerEvents = 'none';
      hiddenVideo.crossOrigin = 'anonymous';
      hiddenVideo.preload = 'metadata'; // Only load metadata, not full video
      hiddenVideo.muted = true;
      hiddenVideo.playsInline = true;
      hiddenVideoRef.current = hiddenVideo;
      document.body.appendChild(hiddenVideo);
    }

    const hiddenVideo = hiddenVideoRef.current;
    isExtractingFrame.current = true;

    const extractEarlyFrame = () => {
      if (hasExtractedFrame.current) return;

      try {
        // Check if video has enough data to extract a frame
        if (hiddenVideo.readyState < 2) return; // HAVE_CURRENT_DATA

        // Create canvas if it doesn't exist
        if (!canvasRef.current) {
          canvasRef.current = document.createElement('canvas');
        }
        const canvas = canvasRef.current;

        // Set canvas dimensions to match video
        const videoWidth = hiddenVideo.videoWidth || 1920;
        const videoHeight = hiddenVideo.videoHeight || 1080;

        if (videoWidth === 0 || videoHeight === 0) return; // Wait for video dimensions

        canvas.width = videoWidth;
        canvas.height = videoHeight;

        // Draw first frame to canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Store original time and state
        const originalTime = hiddenVideo.currentTime;
        const wasPaused = hiddenVideo.paused;

        // Ensure video is paused for extraction
        if (!wasPaused) {
          hiddenVideo.pause();
        }

        // Seek to first frame (0.1s to avoid black frames)
        const targetTime = Math.min(0.1, hiddenVideo.duration > 0 ? hiddenVideo.duration / 10 : 0.1);
        hiddenVideo.currentTime = targetTime;

        // Wait for seek to complete
        const onSeeked = () => {
          try {
            ctx.drawImage(hiddenVideo, 0, 0, canvas.width, canvas.height);

            // Convert canvas to data URL
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
            // Store as client-extracted fallback if no prop fallback exists
            if (!fallbackImage) {
              setClientExtractedFallback(dataUrl);
            }
            setFirstFrameUrl(dataUrl);
            hasExtractedFrame.current = true;
            isExtractingFrame.current = false;

            // Clean up hidden video
            hiddenVideo.removeEventListener('seeked', onSeeked);
            hiddenVideo.removeEventListener('loadedmetadata', onLoadedMetadata);
            hiddenVideo.removeEventListener('loadeddata', onLoadedData);
            hiddenVideo.removeEventListener('canplay', onCanPlay);

            // Remove hidden video from DOM
            if (hiddenVideo.parentNode) {
              hiddenVideo.parentNode.removeChild(hiddenVideo);
            }
            hiddenVideoRef.current = null;
          } catch (err) {
            console.warn('[BackgroundVideo] Failed to extract early first frame:', err);
            isExtractingFrame.current = false;
            hiddenVideo.removeEventListener('seeked', onSeeked);
          }
        };

        hiddenVideo.addEventListener('seeked', onSeeked, { once: true });
      } catch (err) {
        console.warn('[BackgroundVideo] Error extracting early first frame:', err);
        isExtractingFrame.current = false;
      }
    };

    const onLoadedMetadata = () => {
      // Try to extract after metadata is loaded (dimensions available)
      extractEarlyFrame();
    };

    const onLoadedData = () => {
      extractEarlyFrame();
    };

    const onCanPlay = () => {
      extractEarlyFrame();
    };

    // Set video source and start loading metadata
    hiddenVideo.src = normalizedVideoUrl;
    hiddenVideo.load(); // Start loading metadata

    // Listen for events
    hiddenVideo.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
    hiddenVideo.addEventListener('loadeddata', onLoadedData, { once: true });
    hiddenVideo.addEventListener('canplay', onCanPlay, { once: true });

    // Fallback: try extraction after a delay
    const timeoutId = setTimeout(() => {
      if (!hasExtractedFrame.current && hiddenVideo.readyState >= 2) {
        extractEarlyFrame();
      }
      isExtractingFrame.current = false;
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      if (hiddenVideo) {
        hiddenVideo.removeEventListener('loadedmetadata', onLoadedMetadata);
        hiddenVideo.removeEventListener('loadeddata', onLoadedData);
        hiddenVideo.removeEventListener('canplay', onCanPlay);
        if (hiddenVideo.parentNode) {
          hiddenVideo.parentNode.removeChild(hiddenVideo);
        }
      }
      hiddenVideoRef.current = null;
      isExtractingFrame.current = false;
    };
  }, [normalizedVideoUrl, poster, isInView, fallbackImage, clientExtractedFallback, isMobile]);

  // Extract first frame from main video as fallback (if early extraction didn't work)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !normalizedVideoUrl) return;
    if (!isInView) return; // Don't extract if video is not in viewport
    if (hasExtractedFrame.current) return; // Already extracted
    if (poster) return; // Poster provided, skip extraction

    const extractFirstFrame = () => {
      if (hasExtractedFrame.current) return;
      
      try {
        // Check if video has enough data to extract a frame
        if (video.readyState < 2) return; // HAVE_CURRENT_DATA
        
        // Create canvas if it doesn't exist
        if (!canvasRef.current) {
          canvasRef.current = document.createElement('canvas');
        }
        const canvas = canvasRef.current;
        
        // Set canvas dimensions to match video
        const videoWidth = video.videoWidth || video.clientWidth || 1920;
        const videoHeight = video.videoHeight || video.clientHeight || 1080;
        
        if (videoWidth === 0 || videoHeight === 0) return; // Wait for video dimensions
        
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        
        // Draw first frame to canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        // Only extract frame if video is not playing or is at the beginning
        // This avoids interrupting playback
        const currentTime = video.currentTime;
        const wasPlaying = !video.paused;
        const isAtStart = currentTime < 0.5; // Within first 0.5 seconds
        
        // If video is already playing and not at start, skip extraction
        // (video will fade in naturally)
        if (wasPlaying && !isAtStart && videoLoaded) {
          return;
        }
        
        // Pause video temporarily to extract frame (only if needed)
        if (wasPlaying && !isAtStart) {
          video.pause();
        }
        
        // Seek to first frame (0.1s to avoid black frames)
        const targetTime = isAtStart ? currentTime : 0.1;
        video.currentTime = targetTime;
        
        // Wait for seek to complete
        const onSeeked = () => {
          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Convert canvas to data URL
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
            setFirstFrameUrl(dataUrl);
            hasExtractedFrame.current = true;
            
            // Restore original time and play state only if we changed it
            if (!isAtStart) {
              video.currentTime = currentTime;
              if (wasPlaying) {
                video.play().catch(() => {
                  // Ignore play errors
                });
              }
            }
            
            // Clean up
            video.removeEventListener('seeked', onSeeked);
          } catch (err) {
            console.warn('[BackgroundVideo] Failed to extract first frame:', err);
            video.removeEventListener('seeked', onSeeked);
            // Restore original time on error
            if (!isAtStart) {
              video.currentTime = currentTime;
              if (wasPlaying) {
                video.play().catch(() => {
                  // Ignore play errors
                });
              }
            }
          }
        };
        
        video.addEventListener('seeked', onSeeked, { once: true });
      } catch (err) {
        console.warn('[BackgroundVideo] Error extracting first frame:', err);
      }
    };

    // Try to extract frame when video has loaded enough data
    const onLoadedData = () => {
      extractFirstFrame();
    };
    
    const onCanPlay = () => {
      extractFirstFrame();
    };
    
    const onLoadedMetadata = () => {
      // Try to extract after metadata is loaded (dimensions available)
      extractFirstFrame();
    };

    // If video already has data, extract immediately
    if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
      extractFirstFrame();
    } else {
      video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
      video.addEventListener('loadeddata', onLoadedData, { once: true });
      video.addEventListener('canplay', onCanPlay, { once: true });
    }

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, [normalizedVideoUrl, poster, isInView, videoLoaded]);

  // Update ref when prop changes
  useEffect(() => {
    onVideoEventRef.current = onVideoEvent;
  }, [onVideoEvent]);

  // Respect user preferences: reduced motion and data saver
  const [respectUserPrefersStill, setRespectUserPrefersStill] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return; // avoid SSR diffs

    try {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;

      const updatePref = () => {
        const reduced = mediaQuery?.matches === true;
        const saveData = Boolean(connection?.saveData);
        setRespectUserPrefersStill(reduced || saveData);
      };

      updatePref();
      mediaQuery?.addEventListener?.('change', updatePref);
      (connection as { addEventListener?: (event: string, handler: () => void) => void })?.addEventListener?.('change', updatePref);

      return () => {
        mediaQuery?.removeEventListener?.('change', updatePref);
        (connection as { removeEventListener?: (event: string, handler: () => void) => void })?.removeEventListener?.('change', updatePref);
      };
    } catch {
      // noop
    }
  }, []);

  // Load video with retry + absolute fallback URLs
  useEffect(() => {
    if (disableVideo) return;
    // CRITICAL: Hero sections (CRITICAL priority) load immediately, don't wait for in-view
    // Other sections wait for in-view (lazy loading)
    if (priority !== 'CRITICAL' && !isInView) return;
    if (respectUserPrefersStill) return;
    if (typeof window === 'undefined') return;
    // CRITICAL: For Hero sections, don't wait for mobile detection - use synchronous detection
    // REMOVED: hasDecidedMobile check for CRITICAL priority - now uses synchronous detection
    if (priority !== 'CRITICAL' && !hasDecidedMobile) return;

    const video = videoRef.current;
    if (!video) return;
    // CRITICAL: For CRITICAL videos, ensure we have a valid URL (not empty string)
    if (!normalizedVideoUrl || normalizedVideoUrl.trim() === '') {
      // For CRITICAL videos, try to resolve URL directly from videoUrl if normalizedVideoUrl is empty
      if (priority === 'CRITICAL' && videoUrl) {
        // URL will be resolved in the next render cycle, skip this attempt
        return;
      }
      return;
    }

    // DEDUPLICATION: Skip if this video is already loading or was recently loaded
    // This prevents multiple BackgroundVideo instances from triggering duplicate downloads
    const alreadyHandled = checkVideoAlreadyHandled(normalizedVideoUrl);
    if (alreadyHandled) {
      // Video is already being handled by another instance
      // Just set the src and let the browser use its cache
      if (video.src !== normalizedVideoUrl) {
        video.src = normalizedVideoUrl;
        // Try to play from cache (immediate on mobile, deferred on desktop)
        playVideoIdle(video, undefined, isMobile);
      }
      return;
    }

    onVideoEventRef.current?.({ 
      type: 'attempt', 
      detail: { isMobile, url: normalizedVideoUrl } 
    });

    let isCancelled = false;

    // Mark this video as loading to prevent duplicate loads
    markVideoLoading(normalizedVideoUrl);

    const config = getProductionVideoConfig();

    const loadVideo = async () => {
      try {
          // Ensure mobile autoplay prerequisites are set before any load/play
        // Use unified setup function
        setupVideoForMobileAutoplay(video);
            // OPTIMIZED: Use metadata preload for CRITICAL videos, then call load() programmatically
            video.preload = preloadStrategy;
            video.crossOrigin = 'anonymous';
            
            // OPTIMIZED: For CRITICAL videos, call load() immediately after setting preload="metadata"
            // This gives us better control over when video starts loading
            if (priority === 'CRITICAL' && preloadStrategy === 'metadata') {
              // Ensure src is set before calling load()
              if (video.src !== normalizedVideoUrl) {
                video.src = normalizedVideoUrl;
              }
              video.load();
          }

        // Use fallback URLs from videoUrl if available
        // Only use fallbacks if we have a single video URL (not responsive)
        // For responsive videos (mobile/desktop), only load the appropriate one
        const isResponsiveVideo = videoUrl && typeof videoUrl === 'object' && (videoUrl.mobile || videoUrl.desktop);
        const fallbackUrls = isResponsiveVideo 
          ? [] // Don't use fallbacks for responsive videos - only load the appropriate one
          : (videoFallbackUrls.length > 0 ? videoFallbackUrls : []);

        // Fast-fail on mobile to switch sources quicker
        const success = await VideoLoader.loadVideoWithRetry(video, normalizedVideoUrl, {
          maxRetries: isMobile ? Math.min(2, config.maxRetries) : config.maxRetries,
          retryDelay: isMobile ? Math.min(800, config.retryDelay) : config.retryDelay,
          timeoutMs: isMobile ? Math.min(8000, config.timeoutMs) : config.timeoutMs,
          fallbackUrls,
          useConnectivityProbe: false,
        });

        if (isCancelled) {
          // Mark as complete even if cancelled to allow future loads
          markVideoComplete(normalizedVideoUrl, false);
          return;
        }

        if (success) {
          // Mark video as successfully loaded in global tracker
          markVideoComplete(normalizedVideoUrl, true);
          
          setVideoLoaded(true);
          setVideoError(false);
          
          // Check if video is ready for playback (readyState >= 4 = HAVE_ENOUGH_DATA)
          const checkVideoReady = () => {
            if (video.readyState >= 4) {
              setVideoReadyForPlayback(true);
            } else {
              // Wait for video to have enough data
              video.addEventListener('canplaythrough', () => {
                setVideoReadyForPlayback(true);
              }, { once: true });
            }
          };
          
          checkVideoReady();
          onVideoEventRef.current?.({ type: 'loaded' });

          // Attempt to play(), detect autoplay block (e.g., iOS Low Power Mode)
          // On mobile: play immediately (required for autoplay)
          // On desktop: defer with requestIdleCallback to reduce TBT
          playVideoIdle(video, (err: Error) => {
            // Ignore benign AbortError caused by an immediate pause (e.g., tab hidden)
            const name = err?.name || '';
            if (name === 'AbortError' || document.visibilityState === 'hidden') return;

            setAutoplayBlocked(true);
            onVideoEventRef.current?.({ type: 'autoplay_blocked' });
          }, isMobile);
          
          // Fallback: check if autoplay was blocked after a delay
          window.setTimeout(() => {
            if (!isCancelled && video.paused && document.visibilityState === 'visible') {
              setAutoplayBlocked(true);
              onVideoEventRef.current?.({ type: 'autoplay_blocked' });
            }
          }, 800);

          // Fallback timer: if still paused after a short delay, treat as blocked
          window.setTimeout(() => {
            if (!isCancelled && video.paused && document.visibilityState === 'visible') {
              setAutoplayBlocked(true);
              onVideoEventRef.current?.({ type: 'autoplay_blocked' });
            }
          }, 800);
        } else {
          // Mark video as failed in global tracker
          markVideoComplete(normalizedVideoUrl, false);
          
          setVideoError(true);
          setVideoLoaded(false);
          onVideoEventRef.current?.({ type: 'error', detail: { stage: 'retry_exhausted' } });
        }
      } catch (err) {
        if (isCancelled) {
          markVideoComplete(normalizedVideoUrl, false);
          return;
        }
        
        // Mark video as failed in global tracker
        markVideoComplete(normalizedVideoUrl, false);
        
        setVideoError(true);
        setVideoLoaded(false);
        onVideoEventRef.current?.({ 
          type: 'error', 
          detail: { stage: 'unexpected', error: String(err) } 
        });
      }
    };

    loadVideo();

    return () => {
      isCancelled = true;
    };
  }, [disableVideo, respectUserPrefersStill, hasDecidedMobile, isMobile, normalizedVideoUrl, videoFallbackUrls, isInView]);

  // CRITICAL: Immediate mobile play() trigger for CRITICAL priority videos
  // This ensures videos start playing as soon as they're ready on mobile
  useEffect(() => {
    if (priority !== 'CRITICAL' || !isMobile) return;
    const video = videoRef.current;
    if (!video || !normalizedVideoUrl || !videoLoaded) return;
    
    // Try to play immediately when video loads (mobile autoplay)
    const tryPlay = async () => {
      if (video.readyState >= 2 && video.paused && !videoError && !respectUserPrefersStill && !autoplayBlocked) {
        const played = await tryPlayVideoImmediate(video);
        if (played) {
          setHasPlayed(true);
          setAutoplayBlocked(false);
          
          // CRITICAL: If this is a HERO section (CRITICAL priority), notify heroVideoState
          if (priority === 'CRITICAL' && normalizedVideoUrl) {
            heroVideoState.setPlaying(normalizedVideoUrl);
          }
        }
      }
    };
    
    // Try immediately if already loaded
    if (video.readyState >= 2) {
      tryPlay();
    }
    
    // Also try on video ready events
    video.addEventListener('canplay', tryPlay, { once: true });
    video.addEventListener('loadeddata', tryPlay, { once: true });
    
    return () => {
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('loadeddata', tryPlay);
    };
  }, [priority, isMobile, normalizedVideoUrl, videoLoaded, videoError, respectUserPrefersStill, autoplayBlocked]);

  // Track when video is ready for playback (readyState >= 4 = HAVE_ENOUGH_DATA)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !normalizedVideoUrl) return;

    const checkVideoReady = () => {
      // Video is ready when it has enough data to play through without stopping
      if (video.readyState >= 4) {
        setVideoReadyForPlayback(true);
      }
    };

    const onCanPlayThrough = () => {
      setVideoReadyForPlayback(true);
    };

    const onLoadedData = () => {
      // Check if we have enough data
      if (video.readyState >= 4) {
        setVideoReadyForPlayback(true);
      }
    };

    // On mobile, try to play as soon as video can start playing (canplay event)
    // This is critical for mobile autoplay - don't wait for canplaythrough
    const onCanPlay = () => {
      if (isMobile && video.readyState >= 2 && video.paused && !videoError && !respectUserPrefersStill && !autoplayBlocked) {
        // Try to play immediately on mobile when canplay fires
        playVideoIdle(video, undefined, true);
      }
      // Also check if ready for playback
      if (video.readyState >= 4) {
        setVideoReadyForPlayback(true);
      }
    };

    // Check immediately if video already has data
    checkVideoReady();

    // Listen for events that indicate video is ready
    video.addEventListener('canplaythrough', onCanPlayThrough);
    video.addEventListener('loadeddata', onLoadedData);
    // Add canplay listener for mobile autoplay (fires earlier than canplaythrough)
    video.addEventListener('canplay', onCanPlay);

    return () => {
      video.removeEventListener('canplaythrough', onCanPlayThrough);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, [normalizedVideoUrl, videoLoaded, isMobile, videoError, respectUserPrefersStill, autoplayBlocked]);

  // Basic telemetry for playback events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      setHasPlayed(true);
      onVideoEventRef.current?.({ type: 'play' });

      // CRITICAL: If this is a HERO section (CRITICAL priority), notify heroVideoState
      // This allows other routes' media to start loading only after HERO video is playing
      if (priority === 'CRITICAL' && normalizedVideoUrl) {
        heroVideoState.setPlaying(normalizedVideoUrl);
      }
    };

    const onStalled = () => {
      onVideoEventRef.current?.({ type: 'stalled' });
    };
    const onError = () => {
      onVideoEventRef.current?.({ type: 'error', detail: { stage: 'element_error' } });
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('error', onError);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('error', onError);
    };
  }, [priority, normalizedVideoUrl, isMobile]);

  // Ensure playback resumes when tab becomes visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onVis = () => {
      if (document.visibilityState === 'visible' && videoLoaded && !videoError && !respectUserPrefersStill && !autoplayBlocked) {
        // On mobile: play immediately, on desktop: defer with requestIdleCallback
        playVideoIdle(video, undefined, isMobile);
      }
    };

    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [videoLoaded, videoError, respectUserPrefersStill, autoplayBlocked]);

  // Avoid pauses: auto-resume on pause/ended/stalled when allowed
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const resumeIfAllowed = () => {
      if (videoLoaded && !videoError && !respectUserPrefersStill && !autoplayBlocked) {
        // On mobile: play immediately, on desktop: defer with requestIdleCallback
        playVideoIdle(video, undefined, isMobile);
      }
    };

    const onPause = () => resumeIfAllowed();
    const onEnded = () => resumeIfAllowed();
    const onStalled = () => resumeIfAllowed();

    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('stalled', onStalled);

    return () => {
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('stalled', onStalled);
    };
  }, [videoLoaded, videoError, respectUserPrefersStill, autoplayBlocked]);

  // CRITICAL: Listen for global video-autoplay-trigger event (from VideoAutoplayConsent or user interactions)
  // This is especially important on mobile where videos need user interaction to play
  useEffect(() => {
    if (respectUserPrefersStill) return; // honor reduced motion / save-data
    if (typeof window === 'undefined') return;
    if (!videoLoaded) return; // Wait for video to be loaded

    const video = videoRef.current;
    if (!video) return;

    const handleAutoplayTrigger = () => {
      // Enable autoplay and ensure video attributes are set for mobile autoplay
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('muted', 'true');
      video.setAttribute('autoplay', 'true');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      
      // Try to play the video when user interacts (required for mobile autoplay)
      if (video.paused && !videoError && !respectUserPrefersStill) {
        // Ensure video source is loaded before playing
        if (!video.src && normalizedVideoUrl) {
          video.src = normalizedVideoUrl;
          video.load();
          // Wait for video to have some data before playing
          if (video.readyState < 2) {
            const tryPlayWhenReady = () => {
              playVideoIdle(video, (err: Error) => {
                // If play fails, mark as blocked so fallback shows
                if (err?.name !== 'AbortError' && document.visibilityState === 'visible') {
                  setAutoplayBlocked(true);
                }
              }, true);
            };
            video.addEventListener('canplay', tryPlayWhenReady, { once: true });
            video.addEventListener('loadeddata', tryPlayWhenReady, { once: true });
            return;
          }
        }
        
        playVideoIdle(video, (err: Error) => {
          // If play fails, mark as blocked so fallback shows
          if (err?.name !== 'AbortError' && document.visibilityState === 'visible') {
            setAutoplayBlocked(true);
          }
        }, true); // Always use mobile mode (immediate play) for user interactions
        
        // Check if video started playing after a short delay
        setTimeout(() => {
          if (!video.paused && !autoplayBlocked) {
            setAutoplayBlocked(false);
            setHasPlayed(true);
            onVideoEventRef.current?.({ type: 'play', detail: { source: 'user_interaction' } });
            
            // CRITICAL: If this is a HERO section (CRITICAL priority), notify heroVideoState
            if (priority === 'CRITICAL' && normalizedVideoUrl) {
              heroVideoState.setPlaying(normalizedVideoUrl);
            }
          }
        }, 200);
      }
    };

    // Listen for the global autoplay trigger event
    document.addEventListener('video-autoplay-trigger', handleAutoplayTrigger, { capture: true });

    return () => {
      document.removeEventListener('video-autoplay-trigger', handleAutoplayTrigger, { capture: true });
    };
  }, [videoLoaded, videoError, respectUserPrefersStill, isMobile, normalizedVideoUrl]);

  // If autoplay is blocked, start playback on consent or first interaction
  useEffect(() => {
    if (!autoplayBlocked) return;
    if (respectUserPrefersStill) return; // honor reduced motion / save-data
    if (typeof window === 'undefined') return;

    const video = videoRef.current;
    if (!video) return;

    let handled = false;

    // CRITICAL: Use passive: false for touch events to enable synchronous play() calls on mobile
    // Store options object to reuse when removing listener (TypeScript requires exact match)
    const touchOptions = { capture: true, passive: false } as const;

    const cleanup = () => {
      window.removeEventListener('nuo:consentAccepted', onConsent as EventListener, true);
      document.removeEventListener('pointerdown', onFirstInteraction, true);
      // Use same options when removing as when adding
      document.removeEventListener('touchstart', onFirstInteraction, touchOptions);
      document.removeEventListener('keydown', onFirstInteraction, true);
    };

    const onConsent = () => {
      if (handled) return;
      handled = true;
      cleanup();

      // On mobile: play immediately, on desktop: defer with requestIdleCallback
      playVideoIdle(video, (err: Error) => {
        if (err?.name !== 'AbortError') {
          // keep blocked state; user can try again
        }
      }, isMobile);
      
      // Set success state after a short delay (playVideoIdle handles the actual play)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          setAutoplayBlocked(false);
          onVideoEventRef.current?.({ type: 'play', detail: { source: 'cookie_consent' } });
        }, { timeout: 1000 });
      } else {
        setTimeout(() => {
          setAutoplayBlocked(false);
          onVideoEventRef.current?.({ type: 'play', detail: { source: 'cookie_consent' } });
        }, 100);
      }
    };

    const onFirstInteraction = () => {
      // if cookies already accepted from a previous session, treat first interaction as consent
      try {
        const accepted = localStorage.getItem('nuo_cookie_consent') === '1';
        if (!accepted) return;
      } catch {
        // Ignore localStorage errors
      }

      onConsent();
    };

    window.addEventListener('nuo:consentAccepted', onConsent as EventListener, true);
    document.addEventListener('pointerdown', onFirstInteraction, true);
    // CRITICAL: Use passive: false for touch events to enable synchronous play() calls on mobile
    document.addEventListener('touchstart', onFirstInteraction, touchOptions);
    document.addEventListener('keydown', onFirstInteraction, true);

    return cleanup;
  }, [autoplayBlocked, respectUserPrefersStill]);

  // CRITICAL: On mobile, try to play on first user interaction if video is still paused
  // This ensures videos play even if initial autoplay was silently blocked
  useEffect(() => {
    if (!isMobile) return;
    if (respectUserPrefersStill) return;
    if (typeof window === 'undefined') return;
    if (!videoLoaded) return;
    if (hasPlayed) return; // Already played, no need to retry

    const video = videoRef.current;
    if (!video) return;

    let hasAttempted = false;

    const tryPlayOnFirstInteraction = () => {
      if (hasAttempted) return;
      if (!video.paused) {
        hasAttempted = true;
        return; // Already playing
      }
      if (videoError) return;
      if (video.readyState < 2) return; // Not enough data yet
      
      hasAttempted = true;
      // Try to play immediately on mobile when user first interacts
      playVideoIdle(video, (err: Error) => {
        if (err?.name !== 'AbortError' && document.visibilityState === 'visible') {
          setAutoplayBlocked(true);
          hasAttempted = false; // Allow retry on next interaction
        }
      }, true); // Force mobile mode (immediate play)
    };

    // Only listen for the first interaction that might enable autoplay
    // CRITICAL: Use passive: false for touch events to enable synchronous play() calls on mobile
    const touchOptions = { capture: true, once: true, passive: false } as const;
    const clickOptions = { capture: true, once: true } as const;
    document.addEventListener('touchstart', tryPlayOnFirstInteraction, touchOptions);
    document.addEventListener('click', tryPlayOnFirstInteraction, clickOptions);

    return () => {
      // Use same options when removing as when adding
      document.removeEventListener('touchstart', tryPlayOnFirstInteraction, touchOptions);
      document.removeEventListener('click', tryPlayOnFirstInteraction, clickOptions);
    };
  }, [isMobile, videoLoaded, videoError, respectUserPrefersStill, hasPlayed]);

  // Show fallback color when video failed or disabled
  const fallbackCondition = videoError || respectUserPrefersStill || autoplayBlocked || !videoLoaded;
  const [fallbackReady, setFallbackReady] = useState(false);

  useEffect(() => {
    if (disableFallback) return setFallbackReady(false);
    if (!fallbackCondition) {
      setFallbackReady(false);
      return;
    }

    setFallbackReady(false);
    const timer = window.setTimeout(() => setFallbackReady(true), 2000);

    return () => window.clearTimeout(timer);
  }, [fallbackCondition, disableFallback]);

  const shouldShowFallback = !disableFallback && fallbackReady;

  // Show first frame or fallback image as background while video loads (hide when video starts playing)
  // Priority: fallbackImage (from props) > cached poster from video URL > clientExtractedFallback > firstFrameUrl (extracted) > poster
  // ALWAYS check videoPosterCache for the video URL, even if fallbackImage is not provided
  const [extractedPoster, setExtractedPoster] = useState<string | null>(null);
  
  // Resolve responsive fallback image to mobile/desktop version
  // FIXED: Use synchronous mobile detection to avoid delay
  const resolvedFallbackImageUrl = useMemo(() => {
    if (!fallbackImage) return undefined;
    
    // If it's a string, return as-is
    if (typeof fallbackImage === 'string') {
      return fallbackImage;
    }
    
    // If it's a responsive object, resolve based on device
    if (typeof fallbackImage === 'object' && fallbackImage !== null) {
      // Use synchronous mobile detection (user agent only) instead of viewport size
      // This ensures desktop devices autoplay regardless of window size
      const isMobileSync = isMobileUserAgent();
      
      if (isMobileSync) {
        // On mobile, prefer mobile version, fallback to desktop if mobile doesn't exist
        return fallbackImage.mobile || fallbackImage.desktop;
      } else {
        // On desktop, prefer desktop version, fallback to mobile if desktop doesn't exist
        return fallbackImage.desktop || fallbackImage.mobile;
      }
    }
    
    return undefined;
  }, [fallbackImage]); // Removed isMobile dependency - using synchronous detection
  
  const resolvedFallbackImage = useMemo(() => {
    // PRIORITY 1: Check poster prop first (synchronous, always available immediately)
    if (poster && typeof poster === 'string' && poster.trim()) {
      return poster;
    }
    
    // PRIORITY 2: Check resolved fallback image URL (from fallbackImage prop - synchronous now)
    if (resolvedFallbackImageUrl) {
      // Check if resolved fallback image is actually a video URL
      const mediaType = getMediaTypeFromUrl(resolvedFallbackImageUrl);
      if (mediaType === 'video') {
        // Check videoPosterCache for cached poster
        const cachedPoster = videoPosterCache.getPoster(resolvedFallbackImageUrl);
        if (cachedPoster) {
          return cachedPoster;
        }
        // Use extracted poster if available (from async extraction)
        if (extractedPoster) {
          return extractedPoster;
        }
        return null; // Don't use video URL as fallback image (will show when extraction completes)
      }
      // It's an actual image URL, use it immediately
      return resolvedFallbackImageUrl;
    }
    
    // PRIORITY 3: Check if normalizedVideoUrl has a cached poster
    if (normalizedVideoUrl) {
      const cachedPosterFromVideo = videoPosterCache.getPoster(normalizedVideoUrl);
      if (cachedPosterFromVideo) {
        return cachedPosterFromVideo;
      }
    }
    
    // PRIORITY 4: Use extracted poster if available (from async extraction)
    if (extractedPoster) {
      return extractedPoster;
    }
    
    return null;
  }, [resolvedFallbackImageUrl, normalizedVideoUrl, extractedPoster, poster]);
  
  // Monitor videoPosterCache for when posters become available
  // This ensures we use cached posters from S3FilePreview as soon as they're available
  useEffect(() => {
    if (!normalizedVideoUrl) return;
    
    // Check if already cached
    const cachedPoster = videoPosterCache.getPoster(normalizedVideoUrl);
    if (cachedPoster) {
      setExtractedPoster(cachedPoster);
      return; // Already cached
    }
    
    // Check if already extracting - wait for it to complete
    if (videoPosterCache.isExtracting(normalizedVideoUrl)) {
      // Wait for extraction to complete
      const checkInterval = setInterval(() => {
        const poster = videoPosterCache.getPoster(normalizedVideoUrl);
        if (poster) {
          setExtractedPoster(poster);
          clearInterval(checkInterval);
        }
      }, 200);
      
      // Stop checking after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
      return;
    }
    
    // Also check resolved fallback image if it's a video URL
    if (resolvedFallbackImageUrl) {
      const mediaType = getMediaTypeFromUrl(resolvedFallbackImageUrl);
      if (mediaType === 'video') {
        const cachedPosterFromFallback = videoPosterCache.getPoster(resolvedFallbackImageUrl);
        if (cachedPosterFromFallback) {
          setExtractedPoster(cachedPosterFromFallback);
          return;
        }
      }
    }
    // Note: Poster extraction is handled by the existing firstFrameUrl extraction logic
    // This effect just monitors the cache for when posters become available (e.g., from S3FilePreview)
  }, [normalizedVideoUrl, resolvedFallbackImageUrl]);
  
  const effectiveFallbackImage = resolvedFallbackImage || clientExtractedFallback || firstFrameUrl || poster;
  
  // DEBUG: Log when black background conditions are met
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (!effectiveFallbackImage) {
        console.warn('[BackgroundVideo] Black background detected - effectiveFallbackImage is null', {
          resolvedFallbackImage: resolvedFallbackImage,
          clientExtractedFallback: clientExtractedFallback,
          firstFrameUrl: firstFrameUrl,
          poster: poster,
          fallbackImage: fallbackImage,
          resolvedFallbackImageUrl: resolvedFallbackImageUrl,
          isMobile: isMobile,
          hasDecidedMobile: hasDecidedMobile,
          normalizedVideoUrl: normalizedVideoUrl,
          extractedPoster: extractedPoster,
        });
      }
    }
  }, [effectiveFallbackImage, resolvedFallbackImage, clientExtractedFallback, firstFrameUrl, poster, fallbackImage, resolvedFallbackImageUrl, isMobile, hasDecidedMobile, normalizedVideoUrl, extractedPoster]);
  
  // Preload fallback image for immediate display
  // NOTE: Preload is handled by SectionRenderer for Hero sections (CRITICAL priority)
  // This is kept for non-Hero sections or when BackgroundVideo is used standalone
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Skip preload for CRITICAL priority - handled by SectionRenderer
    if (priority === 'CRITICAL') return;
    
    const preloadImage = (url: string) => {
      if (!url || typeof url !== 'string') return;
      
      // Check if link already exists
      const existingLink = document.querySelector(`link[rel="preload"][as="image"][href="${url}"]`);
      if (existingLink) return;
      
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      if (priority === 'HIGH') {
        link.setAttribute('fetchpriority', 'high');
      }
      document.head.appendChild(link);
    };
    
    // Preload fallbackImage prop (both variants if responsive)
    if (fallbackImage) {
      if (typeof fallbackImage === 'string') {
        preloadImage(fallbackImage);
      } else if (typeof fallbackImage === 'object' && fallbackImage !== null) {
        // Preload both mobile and desktop variants
        if (fallbackImage.mobile) preloadImage(fallbackImage.mobile);
        if (fallbackImage.desktop) preloadImage(fallbackImage.desktop);
      }
    }
    
    // Preload resolved fallback image URL (device-specific)
    if (resolvedFallbackImageUrl && typeof resolvedFallbackImageUrl === 'string') {
      preloadImage(resolvedFallbackImageUrl);
    }
    
    // Preload effective fallback image (includes cached posters, extracted frames, etc.)
    if (effectiveFallbackImage && typeof effectiveFallbackImage === 'string') {
      preloadImage(effectiveFallbackImage);
    }
    
    // Preload poster if provided
    if (poster) {
      preloadImage(poster);
    }
  }, [fallbackImage, resolvedFallbackImageUrl, effectiveFallbackImage, poster, priority]);
  
  // Track when fallback image first appears
  useEffect(() => {
    if (effectiveFallbackImage && fallbackImageStartTime.current === null) {
      fallbackImageStartTime.current = Date.now();
    }
  }, [effectiveFallbackImage]);
  
  // Check if we can hide fallback image (video must be PLAYING + minimum 3 seconds)
  // CRITICAL: Fallback image stays visible until video is actually playing to prevent black background
  useEffect(() => {
    if (!effectiveFallbackImage) {
      setCanHideFallback(false);
      return;
    }
    
    const checkCanHide = () => {
      const video = videoRef.current;
      
      // CRITICAL: Video must be playing (not just ready) before hiding fallback
      const isVideoPlaying = video && !video.paused && !video.ended && video.currentTime > 0;
      
      if (!isVideoPlaying || !videoReadyForPlayback) {
        setCanHideFallback(false);
        return;
      }
      
      if (fallbackImageStartTime.current === null) {
        setCanHideFallback(false);
        return;
      }
      
      const elapsed = Date.now() - fallbackImageStartTime.current;
      // CRITICAL: Hero sections (CRITICAL priority) need longer display time on mobile
      // This prevents black background gap while video loads
      const minDisplayTime = isMobile 
        ? (priority === 'CRITICAL' ? 8000 : 5000) // 8 seconds for Hero on mobile, 5 for others
        : (priority === 'CRITICAL' ? 4000 : 3000); // 4 seconds for Hero on desktop, 3 for others
      
      // CRITICAL: On mobile, also check if video has enough data loaded (readyState >= 3)
      // For Hero sections, require even more data (readyState >= 4) to ensure smooth playback
      // This ensures video is actually ready to play smoothly before hiding fallback
      const isVideoReady = isMobile 
        ? (video && video.readyState >= (priority === 'CRITICAL' ? 4 : 3) && isVideoPlaying) // Mobile: need more data loaded, especially for Hero
        : isVideoPlaying; // Desktop: playing is enough
      
      // Only hide fallback if video is ready/playing AND minimum time has passed
      if (elapsed >= minDisplayTime && isVideoReady && videoReadyForPlayback) {
        setCanHideFallback(true);
      } else {
        // Check again after remaining time
        const remainingTime = minDisplayTime - elapsed;
        if (remainingTime > 0) {
          setTimeout(checkCanHide, remainingTime);
        } else {
          // If time passed but video not ready, check again soon
          // On mobile, check more frequently to catch when video becomes ready
          // For Hero sections, check even more frequently
          const checkInterval = isMobile 
            ? (priority === 'CRITICAL' ? 100 : 200) // Check every 100ms for Hero on mobile
            : 100;
          setTimeout(checkCanHide, checkInterval);
        }
      }
    };
    
    checkCanHide();
  }, [effectiveFallbackImage, videoReadyForPlayback, hasPlayed, isMobile]);
  
  // CRITICAL: Fallback image stays visible until video is fully loaded AND minimum 3 seconds have passed
  // This ensures the page always has visible content and smooth transition
  // Always show fallback image if available - it will hide when video is playing
  // Also check props directly to show immediately even if not yet resolved
  // On mobile, be extra conservative - keep fallback visible longer
  const shouldShowFirstFrame = !!effectiveFallbackImage || !!poster || !!fallbackImage;
  
  // Update fallback image opacity based on canHideFallback state (CSS transition replaces Framer Motion)
  useEffect(() => {
    if (shouldShowFirstFrame) {
      // Hide fallback if video is ready and can be hidden
      const shouldHide = canHideFallback && videoReadyForPlayback && hasPlayed && 
        (!isMobile || (isMobile && videoRef.current && videoRef.current.readyState >= 3));
      setFallbackImageOpacity(shouldHide ? 0 : 1);
    }
  }, [canHideFallback, videoReadyForPlayback, hasPlayed, isMobile, shouldShowFirstFrame]);
  
  // Update video opacity based on fallback condition and video readiness (CSS transition replaces Framer Motion)
  useEffect(() => {
    const fallbackCondition = videoError || respectUserPrefersStill || autoplayBlocked || !videoLoaded;
    if (fallbackCondition) {
      setVideoOpacity(0);
    } else {
      // Video fades in only when it's ready AND fallback can be hidden
      const opacity = effectiveFallbackImage 
        ? (canHideFallback && videoReadyForPlayback ? 1 : 0)
        : (videoLoaded && hasPlayed ? 1 : 0);
      setVideoOpacity(opacity);
    }
  }, [effectiveFallbackImage, canHideFallback, videoReadyForPlayback, videoLoaded, hasPlayed, videoError, respectUserPrefersStill, autoplayBlocked]);
  
  // Update gradient overlay opacity (CSS transition replaces Framer Motion)
  useEffect(() => {
    setGradientOverlayOpacity(1);
  }, []);
  
  // Update dark background opacity (CSS transition replaces Framer Motion)
  useEffect(() => {
    if (!disableFallback && shouldShowFallback && !effectiveFallbackImage) {
      setDarkBackgroundOpacity(1);
    } else {
      setDarkBackgroundOpacity(0);
    }
  }, [disableFallback, shouldShowFallback, effectiveFallbackImage]);
  
  // Mobile-specific: Keep fallback visible longer to account for slower video loading
  const MIN_DISPLAY_TIME = isMobile ? 5000 : 3000; // 5 seconds on mobile, 3 on desktop

  // If fallback or first frame is displayed during loading, do not show black cover
  const shouldShowBlackCover = !shouldShowFallback && !shouldShowFirstFrame && !hasPlayed && videoLoaded === false && disableFallback;

  useEffect(() => {
    if (shouldShowFallback) onVideoEventRef.current?.({ type: 'fallback_shown' });
  }, [shouldShowFallback]);

  // Check if video URL is empty
  const isEmptyVideoUrl = !normalizedVideoUrl || normalizedVideoUrl.trim() === '';

  // CRITICAL: Check props directly (synchronous) - don't wait for async resolution
  // This ensures we don't show black background if a fallback image prop is provided
  const hasImmediateFallback = !!poster || !!fallbackImage;

  // Always use transparent background when fallback image is available
  // CRITICAL: NEVER show black background if any fallback source exists (even if not loaded yet)
  // This prevents black flash on mobile while images are loading
  const getBackgroundStyle = (): React.CSSProperties => {
    // If we have any fallback image (resolved, extracted, or poster), use transparent
    if (effectiveFallbackImage) {
      return { backgroundColor: 'transparent' };
    }
    
    // CRITICAL: Also check props directly (synchronous) - don't wait for async resolution
    // This ensures we don't show black background if a fallback image prop is provided
    if (hasImmediateFallback) {
      // We have a fallback source, even if not resolved yet - use transparent to avoid black flash
      return { backgroundColor: 'transparent' };
    }
    
    // CRITICAL: On mobile, be extra conservative - if we're in a Hero section (CRITICAL priority),
    // NEVER show black background. Always use transparent and let the fallback image show when it loads.
    if (isMobile && priority === 'CRITICAL') {
      // Hero sections should ALWAYS have fallback images - if we don't have one yet, wait for it
      // Use transparent background so fallback image can show immediately when loaded
      return { backgroundColor: 'transparent' };
    }
    
    // DEBUG: Log when black background is being shown
    if (process.env.NODE_ENV === 'development') {
      console.warn('[BackgroundVideo] Showing black background - no fallback image available', {
        hasPoster: !!poster,
        hasFallbackImageProp: !!fallbackImage,
        hasResolvedFallbackImageUrl: !!resolvedFallbackImageUrl,
        hasResolvedFallbackImage: !!resolvedFallbackImage,
        hasClientExtractedFallback: !!clientExtractedFallback,
        hasFirstFrameUrl: !!firstFrameUrl,
        isMobile: isMobile,
        hasDecidedMobile: hasDecidedMobile,
        effectiveFallbackImage: effectiveFallbackImage,
        hasImmediateFallback: hasImmediateFallback,
        priority: priority,
      });
    }
    
    // Only show minimal background when absolutely no fallback image source is available
    // (no prop, no poster, no extraction possible, and not a Hero section)
    return { backgroundColor: '#0a0e27' };
  };

  // Get immediate fallback image URL for rendering (synchronous, from props)
  const immediateFallbackUrl = useMemo(() => {
    // Check poster first (synchronous)
    if (poster && typeof poster === 'string' && poster.trim()) {
      return poster;
    }
    // Check fallbackImage prop (synchronous)
    if (fallbackImage) {
      if (typeof fallbackImage === 'string') {
        return fallbackImage;
      }
      // For responsive, use synchronous mobile detection
      if (typeof fallbackImage === 'object' && fallbackImage !== null) {
        const isMobileSync = isMobileUserAgent();
        return isMobileSync 
          ? (fallbackImage.mobile || fallbackImage.desktop)
          : (fallbackImage.desktop || fallbackImage.mobile);
      }
    }
    return null;
  }, [poster, fallbackImage]);

  return (
    <div ref={containerRef} className={className} style={getBackgroundStyle()}>
      {isEmptyVideoUrl && !disableVideo ? (
        // Show placeholder when no video URL is provided
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300">
          <Camera className="w-16 h-16 text-gray-400 mb-3" />
          <span className="text-base font-medium text-gray-600 mb-1">No background media</span>
          <span className="text-sm text-gray-500">Upload a video or image to display</span>
        </div>
      ) : !disableVideo && (
        <>
          {/* On slow connections, show optimized WebP fallback image instead of video */}
          {isSlow && effectiveFallbackImage && (
            <div className="absolute inset-0 z-10">
              <img
                src={effectiveFallbackImage || immediateFallbackUrl || ''}
                alt=""
                className="w-full h-full object-cover"
                aria-hidden="true"
                loading="eager"
                fetchPriority={priority === 'CRITICAL' ? 'high' : 'auto'}
                sizes="100vw"
                srcSet={(effectiveFallbackImage || immediateFallbackUrl) && typeof (effectiveFallbackImage || immediateFallbackUrl) === 'string' 
                  ? generateImageSrcSet((effectiveFallbackImage || immediateFallbackUrl) as string, [640, 960, 1280, 1920]) 
                  : undefined}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div className={gradientOverlayClassName} />
            </div>
          )}
          
          {/* Fallback image or first frame thumbnail - stays visible until video is loaded + 3 seconds minimum */}
          {/* CRITICAL: Fallback image must stay visible until video is fully loaded behind it AND minimum 3 seconds */}
          {/* Only show if not on slow connection (slow connections show WebP above) */}
          {shouldShowFirstFrame && !isSlow && (
            <div
              className={`fallback-image-container absolute inset-0 z-10 ${fallbackImageOpacity === 0 ? 'fade-out' : ''}`}
              style={{ 
                opacity: fallbackImageOpacity,
                pointerEvents: 'none', // Allow clicks to pass through to video
                // Ensure fallback image is always visible initially
                zIndex: 10,
                // CRITICAL: Prevent any flash by ensuring fallback stays visible
                willChange: 'opacity',
                transition: `opacity ${isMobile ? 1.0 : 0.5}s ease-out`
              }}
            >
              <img
                src={effectiveFallbackImage || immediateFallbackUrl || ''}
                alt=""
                className="w-full h-full object-cover"
                aria-hidden="true"
                loading="eager"
                // OPTIMIZED: Add fetchpriority for CRITICAL priority videos
                fetchPriority={priority === 'CRITICAL' ? 'high' : 'auto'}
                sizes="100vw"
                srcSet={(effectiveFallbackImage || immediateFallbackUrl) && typeof (effectiveFallbackImage || immediateFallbackUrl) === 'string' 
                  ? generateImageSrcSet((effectiveFallbackImage || immediateFallbackUrl) as string, [640, 960, 1280, 1920]) 
                  : undefined}
                onError={(e) => {
                  // If fallback image fails to load, hide it to allow video to show
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div
                className={gradientOverlayClassName}
              />
            </div>
          )}

          {/* Video element - loads behind fallback image, fades in when ready */}
          {/* CRITICAL: Always render video for CRITICAL priority (Hero sections) even on slow connections */}
          {/* For non-CRITICAL videos, only render if not on slow connection (slow connections show WebP fallback above) */}
          {(priority === 'CRITICAL' || !isSlow) && (
          <div
            className={`video-container absolute inset-0 z-20 ${videoOpacity === 1 ? 'fade-in' : ''}`}
            style={{
              opacity: videoOpacity,
              transition: 'opacity 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              willChange: 'opacity'
            }}
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay={!shouldDisableAutoplay}
              loop
              muted
              playsInline
              preload={preloadStrategy}
              poster={effectiveFallbackImage || poster || undefined}
              crossOrigin="anonymous"
              // CRITICAL: For CRITICAL priority videos, always set src (don't wait for isInView or network checks)
              // This ensures videos start loading immediately on mobile, even on slower connections
              // Only set src if we have a valid URL (not empty string)
              src={
                priority === 'CRITICAL' 
                  ? (normalizedVideoUrl && normalizedVideoUrl.trim() !== '' ? normalizedVideoUrl : undefined)
                  : (isInView && !shouldDisableAutoplay && normalizedVideoUrl && normalizedVideoUrl.trim() !== '' ? normalizedVideoUrl : undefined)
              }
              controls={false}
              webkit-playsinline="true"
              aria-hidden="true"
              tabIndex={-1}
              // OPTIMIZATION: Explicit aspect ratio to prevent CLS (Cumulative Layout Shift)
              // Use 16:9 as default (most common video aspect ratio)
              // If video metadata is available, this should be updated dynamically
              style={{
                aspectRatio: '16 / 9',
                objectFit: 'cover',
              }}
              // OPTIMIZED: Add fetchpriority for CRITICAL videos
              {...(priority === 'CRITICAL' ? { fetchPriority: 'high' as const } : {})}
              onLoadStart={() => {
                // Video loading started
              }}
            >
              <track kind="captions" />
              Your browser does not support the video tag.
            </video>

            <div
              className={`gradient-overlay ${gradientOverlayClassName} ${gradientOverlayOpacity === 1 ? 'fade-in' : ''}`}
              style={{
                opacity: gradientOverlayOpacity,
                transition: 'opacity 1.5s ease-out'
              }}
            />
          </div>
          )}
        </>
      )}

      {/* Minimal dark background fallback - only shows when video fails and no fallback image is available */}
      {!disableFallback && shouldShowFallback && !effectiveFallbackImage && (
        <div
          className="absolute inset-0"
          style={{ 
            backgroundColor: '#0a0e27',
            opacity: darkBackgroundOpacity,
            transition: 'opacity 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
        >
          <div
            className={`gradient-overlay ${gradientOverlayClassName} ${gradientOverlayOpacity === 1 ? 'fade-in' : ''}`}
            style={{
              opacity: gradientOverlayOpacity,
              transition: 'opacity 1.5s ease-out'
            }}
          />
        </div>
      )}

      {/* Black cover to avoid showing poster/logo before video or fallback */}
      {/* Only show if we have NO fallback image and video hasn't loaded */}
      {shouldShowBlackCover && !effectiveFallbackImage && (
        <div className="absolute inset-0 bg-black pointer-events-none z-0" />
      )}

      {/* DEBUG: Visual indicator when black background is shown (development only) */}
      {process.env.NODE_ENV === 'development' && !effectiveFallbackImage && !hasImmediateFallback && (
        <div className="absolute top-4 left-4 z-[9999] bg-red-500/80 text-white text-xs p-2 rounded pointer-events-none">
          <div className="font-bold">⚠️ Black Background Detected</div>
          <div className="text-[10px] mt-1">
            <div>Poster: {poster ? '✓' : '✗'}</div>
            <div>FallbackImage: {fallbackImage ? '✓' : '✗'}</div>
            <div>Resolved: {resolvedFallbackImage ? '✓' : '✗'}</div>
            <div>Extracted: {clientExtractedFallback ? '✓' : '✗'}</div>
            <div>FirstFrame: {firstFrameUrl ? '✓' : '✗'}</div>
            <div>Mobile: {isMobile ? 'M' : 'D'} ({hasDecidedMobile ? '✓' : '✗'})</div>
          </div>
        </div>
      )}

      {/* Autoplay blocked alert (dismissible) - disabled by default */}
      {/* Feature disabled - uncomment to enable autoplay blocked banner */}
      {/* {autoplayBlocked && !dismissedAutoplayBanner && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-xl w-[92vw] bg-black/80 text-white backdrop-blur-md border border-white/10 rounded-lg shadow-lg">
          <div className="flex items-start gap-3 p-3">
            <div className="text-sm leading-relaxed">
              Video autoplay is disabled (likely Low Power Mode). For the full experience, turn off Low Power Mode and refresh the page.
            </div>
            <button
              onClick={() => setDismissedAutoplayBanner(true)}
              className="ml-auto text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
            >
              Close
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
}

